import { useForm } from "@mantine/form";
import { useAuth } from "../context/AuthContext";
import {
  PasswordInput,
  Select,
  TextInput,
  Button,
  LoadingOverlay,
} from "@mantine/core";
import PageHeader from "../components/common/PageHeader";
import { FormPanel } from "../components/common/Form/FormRow";
import { createHrUser } from "../api/hrUserApi";
import {
  creationNotification,
  errorNotification,
  samePhoneNumberErroNotif,
} from "../utils/useNotification";
import { unwrap } from "../utils/apiResult";
import styled from "styled-components";
import Container from "../components/common/Container";
import {
  combineValidators,
  validateMaxLength,
  validateMinLength,
  validateNoSpaces,
  validatePhoneNumberIsNumeric,
  validatePhoneNumberStartsWithZero,
  validateRequired,
} from "../utils/validations";
import { useNavigate } from "react-router";
import { useRef, useState } from "react";
import PlusIcon from "../components/common/Icons/PlusIcon";
import CustomLoadingOverlay from "../components/common/CustomLoadingOverlay";

const NewPanelUser = () => {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      name: "",
      phone: "",
      password: "",
      privilege: null,
    },
    validate: {
      name: validateRequired,
      phone: combineValidators(
        validateRequired,
        validatePhoneNumberStartsWithZero,
        validatePhoneNumberIsNumeric,
        validateNoSpaces,
        validateMinLength(11)
      ),
      password: combineValidators(
        validateRequired,
        validateMaxLength(16),
        validateMinLength(4)
      ),
      privilege: (value: any) => (value ? null : "Bu alan zorunludur"),
    },
  });

  const handleSubmit = async (values: object) => {
    if (user) {
      setIsLoading(true);
      try {
        const result = unwrap(await createHrUser({ ...values }));
        if (result.ok) {
          creationNotification("Panel Kullanıcısı", true);
          form.reset();
          return;
        }

        if (result.data?.error === "This phone number is being used") {
          samePhoneNumberErroNotif();
        } else {
          errorNotification({ message: result.message ?? undefined });
        }
      } catch (error) {
        console.error("Error:", error);
        errorNotification();
      } finally {
        // Eskiden setIsLoading(false) yalnızca iki dalda çağrılıyordu; başka bir
        // hata gelirse buton kalıcı olarak "yükleniyor"da takılı kalıyordu.
        setIsLoading(false);
      }
    }
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Yeni Panel Kullanıcısı Kaydı"
        isButtoned
        buttonText="Oluştur"
        buttonIcon={<PlusIcon />}
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "40px",
          /* min-content idi: form sabit 468px olduğu için kap da 468px'e
             çivileniyor, 390px'lik ekranda taşıyordu. */
          width: "100%",
        }}
      >
        <FormPanel
          ref={formRef}
          $gap={15}
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <TextInput
            label="Şirket Kodu"
            value={company?.code}
            radius={10}
            size="md"
            readOnly
          />
          <TextInput
            label="Ad Soyad"
            placeholder="Ad Soyad Giriniz."
            radius={10}
            size="md"
            {...form.getInputProps("name")}
            maxLength={40}
          />
          <Select
            label="Yetki"
            placeholder="Yetki Seçiniz"
            radius={10}
            size="md"
            data={[
              { value: "admin", label: "Admin" },
              { value: "user", label: "User" },
            ]}
            {...form.getInputProps("privilege")}
            value={form.values.privilege || null}
          />
          <TextInput
            label="Telefon"
            placeholder="05*********"
            radius={10}
            size="md"
            maxLength={11}
            {...form.getInputProps("phone")}
          />
          <PasswordInput
            label="Şifre"
            placeholder="Şifre Giriniz."
            radius={10}
            size="md"
            maxLength={16}
            {...form.getInputProps("password")}
          />
        </FormPanel>
      </div>
      <CustomLoadingOverlay visible={isLoading} />
    </Container>
  );
};

export default NewPanelUser;

const PageTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  gap: 12px;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
  color: black;
`;

const TitleSubText = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #828282;
`;
